import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { UseFilterBuilderReturn } from "../hooks/useFilterBuilder";
import TermsInput from "./TermsInput";
import CsvUploadLink from "./CsvUploadLink";
import NotificationMethodCheckboxes from "@/ui-components/NotificationMethodCheckboxes";
import { useEnsureWatchlistAlerts } from "@/shared/hooks/useEnsureWatchlistAlerts";
import DismissibleTip from "@/shared/components/DismissibleTip";
import { useIsDeskUser } from "@/hooks/useIsDeskUser";
import DeskPublishFields from "./DeskPublishFields";
import PublishConfirmDialog from "./PublishConfirmDialog";
import useAuth from "@/hooks/useAuth";

interface QueryBuilderPaneProps {
  builder: UseFilterBuilderReturn;
  onCancel?: () => void;
}

export default function QueryBuilderPane({
  builder,
  onCancel,
}: QueryBuilderPaneProps) {
  /**
   * The keyword expression, opened on request.
   *
   * Plain keywords go in the typing field above now, with everything else a
   * list holds. This box is only for what that can't say: a boolean
   * expression with AND, OR and brackets. It opens by itself for a list that
   * already has one — which the chip input it replaces failed to show at all
   * on an edit, since it read the expression once, before the list loaded,
   * and then wrote its own empty chips back over it.
   */
  const [expressionOpen, setExpressionOpen] = useState(false);
  const showExpression = expressionOpen || builder.searchKeyword.trim() !== "";

  /**
   * Publishing to the Newsquawk library.
   *
   * Not on an edit of the desk member's own list — the server ignores
   * owner_type on PATCH, so the tick would do nothing.
   */
  const isDeskUser = useIsDeskUser();
  const { authenticated } = useAuth();
  const { ensurePopup, ensureSound } = useEnsureWatchlistAlerts();
  /** A tag watchlist edited in place: its tags are all it holds. */
  const tagsOnly = builder.inPlace && builder.editingKind === "tag";
  const showDeskFields =
    isDeskUser && (!builder.isEditing || builder.isNewsquawkOwned);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  /** Publishing is irreversible and live on save — ask on the way in only. */
  const handleSave = () => {
    if (isDeskUser && builder.publishToLibrary && !builder.isNewsquawkOwned) {
      setConfirmingPublish(true);
      return;
    }
    builder.handleSubmit();
  };

  return (
    <div className="h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden">
      {/* Header — name input only */}
      <div className="px-4 py-3 border-b border-gray-700/30">
        <input
          type="text"
          value={builder.queryName}
          onChange={(e) => builder.setQueryName(e.target.value)}
          placeholder="Name this watchlist"
          className="w-full px-3 py-2.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
        />

        {/* What to do here, once.
            The modal opens with a watchlist already in the query and no
            indication of what is expected next — a form someone else
            half-filled. Same dismissible tip as the widget filter panel, so
            the pattern is one someone has likely met already. */}
        <DismissibleTip id="watchlist-builder" className="mt-2">
          Type or paste tickers, companies and topics, and tick watchlists or
          tags on the left to add them. It matches anything in any of them.
        </DismissibleTip>

        {/* Logged out, saving writes nothing — say so before the button
            does. The guided demo arrives with a list the concierge
            assembled; "Build it myself" arrives empty. */}
        {!authenticated && (
          <p className="mt-2 text-xs text-gray-400">
            {builder.fromDraft
              ? "This is what the builder made from your description. Nothing is saved in the demo."
              : "Nothing is saved in the demo."}
          </p>
        )}

        {builder.inPlace && (
          <p className="mt-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-300">
            {builder.inPlaceReason === "ingredient"
              ? "Another of your watchlists includes this one, so it stays a list of tags, and changes here reach that watchlist too. To combine it with terms or other lists, make a new watchlist that includes it."
              : tagsOnly
                ? "Customers use this Newsquawk list as it is, so it stays a list of tags: change its tags here. To combine it with terms or other lists, make a new Newsquawk watchlist that includes it."
                : "Customers use this Newsquawk watchlist as it is, so it stays a watchlist: change its terms here. Tags and other lists can't be added, and tags matched below aren't saved. To combine it, make a new Newsquawk watchlist that includes it."}
          </p>
        )}

        {builder.formErrors.categories && (
          <div className="mt-1.5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {builder.formErrors.categories}
          </div>
        )}
        {builder.formErrors.keywords && (
          <div className="mt-1.5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {builder.formErrors.keywords}
          </div>
        )}
      </div>

      {/* Body (scrollable) */}
      <div className="space-y-5 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4">
        {/* Straight under the name, where the other builders put it. */}
        {showDeskFields && (
          <DeskPublishFields
            publish={builder.publishToLibrary}
            onPublishChange={builder.setPublishToLibrary}
            description={builder.description}
            onDescriptionChange={builder.setDescription}
            whenToUse={builder.whenToUse}
            onWhenToUseChange={builder.setWhenToUse}
            categories={builder.assetClasses}
            onCategoriesChange={builder.setAssetClasses}
            alreadyPublished={builder.isNewsquawkOwned}
          />
        )}

        {/* The typing field: what a watchlist is made of, with the review
            of what the tag lookup made of each word. */}
        {!tagsOnly && (
          <TermsInput
            terms={builder.terms}
            labelAction={
              builder.terms.isResolving ? (
                <span className="text-xs text-gray-500">Looking up…</span>
              ) : (
                <CsvUploadLink
                  onFile={builder.terms.addTickerCsv}
                  label="Upload tickers CSV"
                  title="Every row is added as a ticker. For topics and phrases, type or paste them into the field instead."
                />
              )
            }
          />
        )}

        {/* Query preview */}
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Matches anything in
          </div>
          <QueryPreview builder={builder} />
        </div>

        {/* Not for a Newsquawk list edited in place, which stays what it is. */}
        {!builder.inPlace && (
          <div>
            {showExpression ? (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Keyword expression
                  </div>
                  {!builder.searchKeyword.trim() && (
                    <button
                      type="button"
                      onClick={() => setExpressionOpen(false)}
                      className="cursor-pointer border-none bg-transparent p-0 text-2xs text-gray-500 transition-colors hover:text-gray-300"
                    >
                      Hide
                    </button>
                  )}
                </div>
                <textarea
                  value={builder.searchKeyword}
                  onChange={(e) => builder.setSearchKeyword(e.target.value)}
                  placeholder="(trump AND powell) OR (iran AND strait) OR tariffs"
                  className="w-full px-3 py-2.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500 min-h-[60px] resize-y"
                  rows={2}
                />
                <div className="text-2xs text-gray-500 mt-1.5">
                  Use parentheses to group terms,{" "}
                  <code className="bg-gray-700 px-1 rounded text-gray-300">
                    OR
                  </code>{" "}
                  to match any,{" "}
                  <code className="bg-gray-700 px-1 rounded text-gray-300">
                    AND
                  </code>{" "}
                  to require all.
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setExpressionOpen(true)}
                className="cursor-pointer border-none bg-transparent p-0 text-xs text-squawk-blue transition-colors hover:text-squawk-blue/80"
              >
                Need AND? Add a keyword expression
              </button>
            )}
          </div>
        )}

        {/* Keyword size */}
        {builder.keywordsSize.count > 0 && (
          <div
            className={clsx(
              "rounded border px-3 py-2 text-xs",
              builder.keywordsSize.ok
                ? "border-gray-700 text-gray-400"
                : "border-red-500/40 bg-red-500/10 text-red-300"
            )}
          >
            {builder.keywordsSize.count}/{builder.MAX_KEYWORDS_COUNT} keywords
            {" · "}
            {builder.keywordsSize.totalChars}/{builder.MAX_KEYWORDS_TOTAL_CHARS}{" "}
            chars
          </div>
        )}

        {/* Highlight colour — every list highlights as well as filters. */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Highlight color
          </span>
          <div className="flex items-center gap-2">
            <label className="relative cursor-pointer">
              <span
                className="block size-7 rounded border border-gray-600 hover:border-squawk-blue transition-colors"
                style={{ backgroundColor: builder.displayColor }}
              />
              <input
                type="color"
                value={builder.displayColor}
                onChange={(e) => builder.setColor(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer size-7"
                aria-label="Highlight color"
              />
            </label>
            <input
              type="text"
              value={builder.displayColor}
              onChange={(e) => {
                const v = e.target.value;
                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) builder.setColor(v);
              }}
              maxLength={7}
              className="w-20 px-2 py-1 text-xs font-mono bg-gray-800 text-gray-300 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>
        </div>
        {/* No priority here: which of two lists wins where both match is
            set in each widget's Highlight panel, not on the list. */}

        {builder.hasLibraryOverride && (
          <button
            type="button"
            onClick={builder.resetLibraryLook}
            className="-mt-2 cursor-pointer border-0 bg-transparent p-0 text-xs text-gray-500 transition-colors hover:text-squawk-blue"
          >
            Reset to Newsquawk&apos;s colour
          </button>
        )}

        {/* Notifications */}
        <div>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
            Notifications
          </div>
          {/* Sound and pop-up were "coming soon" because the alert lookups
              only read watchlists; they now read combined watchlists too. */}
          <NotificationMethodCheckboxes
            value={builder.notifications}
            onChange={builder.setNotifications}
            onEnableMethod={(method) => {
              if (method === "sound") ensureSound();
              if (method === "popup") ensurePopup();
            }}
            variant="pane"
          />
        </div>
      </div>

      {/* Footer */}
      <div
        className={clsx(
          "px-4 py-3 border-t border-gray-700/30 flex flex-col sm:flex-row sm:items-center gap-2",
          builder.isEditing ? "sm:justify-between" : "sm:justify-end"
        )}
      >
        {builder.isEditing && (
          <button
            onClick={builder.handleDelete}
            disabled={builder.isDeleting}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded border border-red-800/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors disabled:opacity-50 order-3 sm:order-1"
          >
            {builder.isDeleting ? "Deleting..." : "Delete"}
          </button>
        )}
        {/* What the save will do, said before it happens. A copy of a
            Newsquawk list saves nothing until something changes, and what
            changed decides whether it is kept as the user's look for the
            library list or saved as a list of their own. */}
        {builder.basedOn && (
          <p className="order-2 text-xs text-gray-500 sm:order-1 sm:mr-auto">
            {builder.copyUnchanged
              ? builder.libraryLookable
                ? builder.libraryKind === "watchlist"
                  ? "Change its colour to keep it for you, or its name, terms or alerts to save your own version."
                  : "Change its colour to keep it for you, or its name, lists, tags or alerts to save your own version."
                : `Change something to save your own version. To use “${builder.basedOn}” as it is, pick it from any widget's filter or highlight menu.`
              : builder.lookOnly
                ? `Only for you — “${builder.basedOn}” itself doesn't change.`
                : builder.libraryContentChanged
                  ? builder.libraryKind === "watchlist"
                    ? "You've changed its name, terms or alerts, so this saves a new watchlist of your own."
                    : "You've changed its name, lists, tags or alerts, so this saves a new watchlist of your own."
                  : null}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2">
          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={
              builder.isSubmitting ||
              !builder.queryName.trim() ||
              builder.copyUnchanged
            }
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {builder.isSubmitting
              ? "Saving..."
              : builder.isEditing
                ? "Save changes"
                : builder.basedOn
                  ? builder.libraryLookable && !builder.libraryContentChanged
                    ? "Save colour"
                    : "Save as new watchlist"
                  : "Save watchlist"}
          </button>
        </div>
      </div>

      <PublishConfirmDialog
        open={confirmingPublish}
        listName={builder.queryName.trim() || "Untitled watchlist"}
        onCancel={() => setConfirmingPublish(false)}
        onConfirm={() => {
          setConfirmingPublish(false);
          builder.handleSubmit();
        }}
      />
    </div>
  );
}

// ── Query Preview with visual grouping ─────────────────────────────────

function QueryPreview({ builder }: { builder: UseFilterBuilderReturn }) {
  const typed = builder.terms;
  const hasListsOrTags =
    builder.hasKeywords ||
    builder.hasWatchlistsOrTags ||
    builder.hasMixedWatchlists;
  const hasAnything = typed.hasTerms || hasListsOrTags;

  if (!hasAnything) {
    return (
      <div className="bg-gray-800/50 rounded px-3 py-2.5 text-sm text-gray-500">
        Type tickers, companies or topics above, or tick watchlists and tags
        on the left
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded px-2.5 py-2 space-y-1.5">
      {/* What was typed, as one part: the field above is where it changes.
          Its tags ride along — a typed list matches its words or anything
          tagged as them. */}
      {typed.hasTerms && (
        <div className="border border-gray-600/50 rounded px-2 py-1.5">
          <div className="text-2xs font-medium text-gray-400 uppercase tracking-wider mb-1">
            Typed
          </div>
          <div className="text-xs text-gray-200">
            {[
              plural(typed.tickers.length, "ticker"),
              plural(typed.keywords.length, "keyword"),
              plural(typed.keptTags.length, "matched tag"),
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      )}

      {typed.hasTerms && hasListsOrTags && (
        <div className="flex justify-center">
          <span className="text-2xs font-bold text-gray-400">OR</span>
        </div>
      )}

      {/* Keyword group */}
      {builder.hasKeywords && (
        <div className="border border-purple-800/30 rounded px-2 py-1.5">
          <div className="text-2xs font-medium text-purple-400/70 uppercase tracking-wider mb-1">
            Keywords
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.searchKeyword.trim() && (
              <QueryChip
                label={builder.searchKeyword.trim()}
                accent="border-l-purple-400"
                onRemove={() => builder.setSearchKeyword("")}
              />
            )}
            {builder.selectedGrouped.keywordOnly.map((p, i) => (
              <span key={p.id} className="contents">
                {(i > 0 || builder.searchKeyword.trim()) && (
                  <OperatorToggle
                    value={builder.keywordWatchlistOp}
                    onChange={builder.setKeywordWatchlistOp}
                  />
                )}
                <QueryChip
                  label={p.name}
                  accent="border-l-purple-400"
                  onRemove={() => builder.togglePortfolio(p.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Joiner between keywords and watchlists/tags */}
      {builder.hasKeywords && builder.hasWatchlistsOrTags && (
        <div className="flex justify-center">
          <OperatorToggle
            value={builder.keywordOp}
            onChange={builder.setKeywordOp}
          />
        </div>
      )}

      {/* Watchlists group. Lists holding both tickers and keywords sit here
          too: they are OR'd with everything, and were missing from the
          preview altogether — a list you had ticked showed as nothing. */}
      {(builder.hasTickerWatchlists || builder.hasMixedWatchlists) && (
        <div className="border border-blue-800/30 rounded px-2 py-1.5">
          <div className="text-2xs font-medium text-blue-400/70 uppercase tracking-wider mb-1">
            Watchlists
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              ...builder.selectedGrouped.tickerOnly,
              ...builder.selectedGrouped.mixed,
            ].map((p, i) => (
              <span key={p.id} className="contents">
                {i > 0 && (
                  <span className="text-xs text-gray-400 font-medium px-0.5">
                    OR
                  </span>
                )}
                <QueryChip
                  label={p.name}
                  accent="border-l-blue-400"
                  onRemove={() => builder.togglePortfolio(p.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AND/OR between tickers and tags */}
      {builder.hasTickerWatchlists && builder.hasTags && (
        <div className="flex justify-center">
          <OperatorToggle
            value={builder.filterMode}
            onChange={builder.setFilterMode}
          />
        </div>
      )}
      {/* Lists with tickers and keywords are always OR'd, so no toggle. */}
      {!builder.hasTickerWatchlists &&
        builder.hasMixedWatchlists &&
        builder.hasTags && (
          <div className="flex justify-center">
            <span className="text-2xs font-bold text-gray-400">OR</span>
          </div>
        )}

      {/* Tags group */}
      {builder.hasTags && (
        <div className="border border-green-800/30 rounded px-2 py-1.5">
          <div className="text-2xs font-medium text-green-400/70 uppercase tracking-wider mb-1">
            Tags
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.selectedCategories.map((c, i) => (
              <span key={`c-${c.id}`} className="contents">
                {i > 0 && (
                  <span className="text-xs text-gray-400 font-medium px-0.5">
                    OR
                  </span>
                )}
                <QueryChip
                  label={c.title}
                  accent="border-l-green-400"
                  onRemove={() => builder.toggleCategory(c)}
                />
              </span>
            ))}

            {builder.selectedTagWatchlistObjects.map((tw, i) => (
              <span key={`tw-${tw.id}`} className="contents">
                {(i > 0 || builder.selectedCategories.length > 0) && (
                  <span className="text-xs text-gray-400 font-medium px-0.5">
                    OR
                  </span>
                )}
                <QueryChip
                  label={tw.name}
                  accent="border-l-green-400"
                  onRemove={() => builder.toggleTagWatchlist(tw.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** "3 tickers", "1 keyword" — or nothing for none. */
function plural(count: number, noun: string): string | null {
  if (count === 0) return null;
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

// ── Query Chip ──────────────────────────────────────────────────────────

function QueryChip({
  label,
  accent,
  onRemove,
}: {
  label: string;
  accent: string;
  onRemove: () => void;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-r bg-gray-700/60 border border-gray-600/40 border-l-2 ${accent} text-xs text-gray-100`}
    >
      <span>{label}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-0.5 text-gray-400 hover:text-gray-200 transition-colors"
      >
        <XMarkIcon className="size-3" />
      </button>
    </span>
  );
}

// ── Operator Toggle ─────────────────────────────────────────────────────

function OperatorToggle({
  value,
  onChange,
}: {
  value: "AND" | "OR";
  onChange: (v: "AND" | "OR") => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(value === "AND" ? "OR" : "AND")}
      className="px-2 py-0.5 text-2xs font-bold rounded border border-squawk-blue/50 bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors cursor-pointer whitespace-nowrap focus:outline-none"
      title={`Click to toggle to ${value === "AND" ? "OR" : "AND"}`}
    >
      {value}&#x25BE;
    </button>
  );
}
